import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  IconButton,
} from '@material-ui/core';
import { Button, useNotify } from 'react-admin';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import {
  DAYS,
  serializePlanning,
  deserializePlanning,
  timeToMinutes,
  minutesBetween,
  toHumanDuration,
  hmsToMinutes,
} from './planningUtils';

const generateId = () => {
  return `${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
};

const ensureIds = (planningObj) => {
  const withIds = {};
  Object.keys(planningObj || {}).forEach((dayKey) => {
    const arr = Array.isArray(planningObj[dayKey]) ? planningObj[dayKey] : [];
    withIds[dayKey] = arr.map((s) => {
      return {
        id: s.id || generateId(),
        start: s.start,
        end: s.end,
      };
    });
  });
  return withIds;
};

const emptyPlanning = () => {
  return DAYS.reduce((acc, d) => {
    // eslint-disable-next-line no-unused-expressions
    acc.key ? (acc[d.key] = []) : null; // safeguard
    acc[d.key] = [];
    return acc;
  }, {});
};

const normalizePlanning = (obj) => {
  const base = emptyPlanning();
  const data = deserializePlanning(obj);
  return { ...base, ...data };
};

const validateShift = (shift) => {
  if (!shift.start || !shift.end) {
    return false;
  }
  return shift.start < shift.end;
};

const hasOverlap = (shifts) => {
  if (!Array.isArray(shifts)) {
    return false;
  }
  const valids = shifts.filter(validateShift);
  if (valids.length <= 1) {
    return false;
  }
  const sorted = [...valids].sort((a, b) => {
    return timeToMinutes(a.start) - timeToMinutes(b.start);
  });
  for (let i = 1; i < sorted.length; i += 1) {
    const prevEnd = timeToMinutes(sorted[i - 1].end);
    const currentStart = timeToMinutes(sorted[i].start);
    if (currentStart < prevEnd) {
      return true; // overlap detected
    }
  }
  return false;
};

const mergeContiguous = (sortedShifts) => {
  if (!Array.isArray(sortedShifts) || sortedShifts.length === 0) return [];
  const res = [];
  for (let i = 0; i < sortedShifts.length; i += 1) {
    const cur = sortedShifts[i];
    if (!cur || !cur.start || !cur.end) continue;
    if (res.length === 0) {
      res.push({ ...cur });
      continue;
    }
    const last = res[res.length - 1];
    if (last.end === cur.start) {
      last.end = cur.end;
    } else {
      res.push({ ...cur });
    }
  }
  return res;
};

const ShiftRow = ({ dayKey, index, shift, onChange, onDelete }) => {
  return (
    <div
      style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0' }}
    >
      <input
        type="time"
        value={shift.start || ''}
        onChange={(e) => {
          onChange(dayKey, index, { ...shift, start: e.target.value });
        }}
      />
      <span>-</span>
      <input
        type="time"
        value={shift.end || ''}
        onChange={(e) => {
          onChange(dayKey, index, { ...shift, end: e.target.value });
        }}
      />
      <IconButton
        aria-label="delete"
        onClick={() => {
          onDelete(dayKey, index);
        }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </div>
  );
};

const WeeklyPlanningDialog = ({
  open,
  onClose,
  onSave,
  initialSerialized,
  totalWorkTime,
}) => {
  const notify = useNotify();

  const initialPlanning = useMemo(() => {
    return ensureIds(normalizePlanning(initialSerialized));
  }, [initialSerialized]);

  const [planning, setPlanning] = useState(initialPlanning);

  React.useEffect(() => {
    setPlanning(ensureIds(normalizePlanning(initialSerialized)));
  }, [initialSerialized, open]);

  const handleAddShift = (dayKey) => {
    setPlanning((prev) => {
      const copy = { ...prev };
      copy[dayKey] = [
        ...(copy[dayKey] || []),
        { id: generateId(), start: '06:00', end: '09:00' },
      ];
      return copy;
    });
  };

  const handleChangeShift = (dayKey, index, newShift) => {
    setPlanning((prev) => {
      const copy = { ...prev };
      const arr = [...(copy[dayKey] || [])];
      const prevShift = arr[index] || {};
      arr[index] = {
        ...prevShift,
        ...newShift,
        id: prevShift.id || newShift.id || generateId(),
      };
      copy[dayKey] = arr;
      return copy;
    });
  };

  const handleDeleteShift = (dayKey, index) => {
    setPlanning((prev) => {
      const copy = { ...prev };
      const arr = [...(copy[dayKey] || [])];
      arr.splice(index, 1);
      copy[dayKey] = arr;
      return copy;
    });
  };

  const handleSave = () => {
    const overlappingDays = DAYS.filter((d) => {
      return hasOverlap(planning[d.key] || []);
    }).map((d) => {
      return d.label;
    });

    if (overlappingDays.length > 0) {
      notify(
        `Les tranches horaires se chevauchent pour: ${overlappingDays.join(
          ', ',
        )}.\nMerci d'ajuster les horaires pour chaque jour afin d'éviter les chevauchements.`,
        { type: 'error', autoHideDuration: 3000 },
      );
      return;
    }

    const cleaned = DAYS.reduce((acc, d) => {
      const valid = (planning[d.key] || []).filter((s) => {
        return s.start && s.end && s.start < s.end;
      });
      if (valid.length) {
        const sorted = [...valid].sort((a, b) => {
          return timeToMinutes(a.start) - timeToMinutes(b.start);
        });
        const merged = mergeContiguous(sorted);
        if (merged.length) {
          acc[d.key] = merged.map(({ start, end }) => {
            return { start, end };
          });
        }
      }
      return acc;
    }, {});

    onSave(serializePlanning(cleaned));
  };

  const hasAny = Object.values(planning || {}).some((arr) => {
    return (arr || []).length > 0;
  });

  const assignedMinutes = useMemo(() => {
    try {
      return DAYS.reduce((acc, d) => {
        const shifts = Array.isArray(planning?.[d.key]) ? planning[d.key] : [];
        const dayMinutes = (shifts || [])
          .filter((s) => {
            return s && s.start && s.end && s.start < s.end;
          })
          .reduce((sum, s) => {
            return sum + minutesBetween(s.start, s.end);
          }, 0);
        return acc + dayMinutes;
      }, 0);
    } catch (e) {
      return 0;
    }
  }, [planning]);

  const totalWorkMinutes = useMemo(() => {
    return hmsToMinutes(totalWorkTime);
  }, [totalWorkTime]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="weekly-planning-title"
    >
      <DialogTitle id="weekly-planning-title">
        {'Planning hebdomadaire — '}
        <span
          style={{
            color:
              assignedMinutes === totalWorkMinutes
                ? 'green'
                : assignedMinutes > totalWorkMinutes
                ? 'red'
                : 'orange',
          }}
        >
          {toHumanDuration(assignedMinutes)} /{' '}
          {toHumanDuration(totalWorkMinutes || 0)}
        </span>
      </DialogTitle>
      <DialogContent>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {DAYS.map((d) => {
            const shifts = planning[d.key] || [];
            return (
              <FormControl
                key={d.key}
                component="fieldset"
                style={{
                  border: '1px solid #eee',
                  padding: 12,
                  borderRadius: 6,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <FormLabel component="legend">{d.label}</FormLabel>
                  <button
                    type="button"
                    style={{
                      padding: '6px 10px',
                      borderRadius: 4,
                      border: '1px solid #ccc',
                      background: 'white',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      handleAddShift(d.key);
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      <AddIcon fontSize="small" /> Ajouter une tranche
                    </span>
                  </button>
                </div>
                <div style={{ marginTop: 8 }}>
                  {shifts.length === 0 ? (
                    <div style={{ color: '#777', fontSize: 12 }}>
                      Aucune tranche horaire
                    </div>
                  ) : (
                    shifts.map((s, idx) => {
                      return (
                        <ShiftRow
                          key={s.id || `${d.key}-${idx}`}
                          dayKey={d.key}
                          index={idx}
                          shift={s}
                          onChange={handleChangeShift}
                          onDelete={handleDeleteShift}
                        />
                      );
                    })
                  )}
                </div>
              </FormControl>
            );
          })}
        </div>
      </DialogContent>
      <DialogActions>
        <Button label="Annuler" onClick={onClose} />
        <Button
          label={hasAny ? 'Enregistrer' : 'Enregistrer (vide)'}
          onClick={handleSave}
          variant="contained"
        />
      </DialogActions>
    </Dialog>
  );
};

WeeklyPlanningDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  initialSerialized: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

WeeklyPlanningDialog.defaultProps = {
  initialSerialized: '',
};

export default WeeklyPlanningDialog;
